import "core-js/features/array/at";
import { debounce } from "lodash";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";

import { LISSpecimen, SpecimenProtocolOverride } from "pwapi";

import { useIsLoading } from "shared/hooks/requestStatusHooks";

import { updateSpecimen } from "app/actions/specimenActions";
import { useAppDispatch } from "app/hooks";
import {
  GrossingConditional,
  grossingConditionals,
} from "util/grossingMadLibs/conditionals";
import {
  isShaveFragments,
  isExcisionWithTips,
  isMultipleShavesInSingleCassette,
  isOrientedEllipse,
  isEyeWedge,
  isFalseMargins,
  isDIFSelected,
} from "util/grossingMadLibs/conditions";
import { grossingModifiers } from "util/grossingMadLibs/modifiers";
import {
  GrossingSegment,
  RepeatingGrossingSegments,
  grossingSegments as grossingSegmentsSkin,
} from "util/grossingMadLibs/segments";
import { emptySegment } from "util/grossingMadLibs/segmentsCommon";
import { grossingSegments as grossingSegmentsNail } from "util/grossingMadLibs/segmentsNail";
import { grossingSegments as grossingSegmentsSlowMohs } from "util/grossingMadLibs/segmentsSlowMohs";
import {
  GrossingModifierSelections,
  GrossingSelections,
  GrossingTokenSelection,
  GrossingTokenSelections,
} from "util/grossingMadLibs/selections";
import { GrossingToken, grossingTokens } from "util/grossingMadLibs/tokens";

import { useSelectedLab } from "./useSelectedLabStation";

const useGrossingMadLib = ({
  specimen,
  complete = false,
}: {
  specimen: LISSpecimen;
  complete?: boolean;
}) => {
  const lab = useSelectedLab();

  const isNail = specimen.protocols?.includes(
    SpecimenProtocolOverride.protocol.NAIL
  );
  const isSlowMohs = specimen.protocols?.includes(
    SpecimenProtocolOverride.protocol.SLOW_MOHS
  );

  const dispatch = useAppDispatch();
  const [modifierSelections, setModifierSelections] =
    useState<GrossingModifierSelections>(
      () => specimen.grossing_modifier_selections || {}
    );
  const toggleModifier = (name: keyof typeof grossingModifiers) => {
    if (!modifierSelections[name]) {
      const modifier = grossingModifiers[name];

      // If turning this modifier on should turn off other modifiers, do that here
      const newModifierSelections = (modifier.turnsOff || []).reduce((s, m) => {
        const { [m]: _, ...s2 } = s;
        return s2;
      }, modifierSelections);

      // If turning this modifier on should turn on other modifiers, do that here
      (modifier.turnsOn || []).forEach((m) => {
        newModifierSelections[m] = true;
      });

      // Turn on this modifier
      setModifierSelections({
        ...newModifierSelections,
        [name]: true,
      });
    } else {
      const { [name]: _, ...newModifierSelections } = modifierSelections;
      setModifierSelections(newModifierSelections);
    }
  };

  const [currentToken, setCurrentTokenState] = useState<
    keyof typeof grossingTokens | undefined
  >();

  const userHasChangedTokenRef = useRef(false);
  const setCurrentToken = (token: typeof currentToken, wasUser = true) => {
    if (wasUser) {
      if (token === undefined) {
        userHasChangedTokenRef.current = false;
      } else if (currentToken !== undefined) {
        userHasChangedTokenRef.current = true;
      }
    }
    setCurrentTokenState(token);
  };

  const [tokenSelections, setTokenSelections] =
    useState<GrossingTokenSelections>(
      () => specimen.grossing_token_selections || {}
    );

  const [labStainSelections, setLabStainSelections] = useState<number[]>(() => {
    // @ts-ignore
    if (specimen.grossing_lab_stain_selections)
      // @ts-ignore
      return specimen.grossing_lab_stain_selections as number[];

    return [];
  });

  const toggleLabStain = (id: number) => {
    if (labStainSelections.includes(id)) {
      setLabStainSelections(labStainSelections.filter((i) => i !== id));
    } else {
      setLabStainSelections([...labStainSelections, id]);
    }
  };

  const [labPanelSelections, setLabPanelSelections] = useState<number[]>(() => {
    // @ts-ignore
    if (specimen.grossing_lab_panel_selections)
      // @ts-ignore
      return specimen.grossing_lab_panel_selections as number[];

    return [];
  });

  const toggleLabPanel = (id: number) => {
    if (labPanelSelections.includes(id)) {
      setLabPanelSelections(labPanelSelections.filter((i) => i !== id));
    } else {
      setLabPanelSelections([...labPanelSelections, id]);
    }
  };

  const selections = useMemo(
    () => ({
      specimen,
      tokenSelections,
      modifierSelections,
      labStainSelections,
    }),
    [specimen, tokenSelections, modifierSelections, labStainSelections]
  );

  const cassetteCount = isMultipleShavesInSingleCassette(selections)
    ? 1
    : parseInt(tokenSelections.cassette_count?.value || "1");

  // Set conditional defaults.
  // These defaults do not come from the backend because they are only needed when certain
  // conditions are met (usually a GrossingModifier). Their values are only persisted if
  // changed by the user.

  const newTokenSelections: GrossingTokenSelections = {};

  // When swapping to DIF specimens reset to just type, medium, source, and cassette_count. This ensure any values previously set that are not used by DIF are removed.
  const prevDifRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (!prevDifRef.current && isDIFSelected(selections)) {
      setTokenSelections({
        type: {
          value: tokenSelections.type?.value || "shave",
          custom: false,
          default: true,
        },
        medium: {
          value: "Michel's medium",
          custom: false,
          default: true,
        },
        source: {
          value: tokenSelections.source?.value || "skin",
          custom: true,
          default: true,
        },
        cassette_count: {
          value: "1",
          custom: false,
          default: true,
        },
      });
    }

    prevDifRef.current = isDIFSelected(selections);
  }, [selections]);

  // Set default values for orientation tokens only if the modifier is selected
  if (isOrientedEllipse(selections)) {
    if (!tokenSelections.orienting_mark) {
      newTokenSelections.orienting_mark = {
        value: "suture",
        default: true,
        custom: false,
      };
    }
    ["blue", "green", "black", "red", "yellow"].forEach((color) => {
      const tokenKey = `orientation_color_${color}`;
      if (!tokenSelections[tokenKey]) {
        newTokenSelections[tokenKey] = {
          value: color,
          default: true,
          custom: false,
        };
      }
    });
  }

  // Set default values for eye wedge tokens only if the modifier is selected
  if (isEyeWedge(selections)) {
    if (!tokenSelections.ew_medial_color) {
      newTokenSelections.ew_medial_color = {
        value: "green",
        default: true,
        custom: false,
      };
    }
    if (!tokenSelections.ew_lateral_color) {
      newTokenSelections.ew_lateral_color = {
        value: "blue",
        default: true,
        custom: false,
      };
    }
    if (!tokenSelections.ew_false_color) {
      newTokenSelections.ew_false_color = {
        value: "red",
        default: true,
        custom: false,
      };
    }
  }

  // Set default values for false margins only if the modifier is selected
  if (isFalseMargins(selections)) {
    if (!tokenSelections.fm_color) {
      newTokenSelections.fm_color = {
        value: "orange",
        default: true,
        custom: false,
      };
    }
  }

  // Fix invalid cassette choice/ranges if necessary
  Object.keys(tokenSelections).forEach((tokenKey) => {
    if (
      (tokenKey.endsWith("cassette_choice") ||
        tokenKey.endsWith("cassette_range")) &&
      parseInt(tokenSelections[tokenKey].value || "1") >
        (parseInt(tokenSelections.cassette_count?.value || "0") || 9)
    ) {
      newTokenSelections[tokenKey] = {
        value: undefined,
        default: false,
        custom: false,
      };
    }
    if (
      tokenKey.endsWith("cassette_range") &&
      parseInt(tokenSelections[tokenKey].value || "10") <=
        (parseInt(
          tokenSelections[tokenKey.replace("_range", "_choice")].value || "0"
        ) || 9)
    ) {
      newTokenSelections[tokenKey] = {
        value: undefined,
        default: false,
        custom: false,
      };
    }
  });

  if (Object.keys(newTokenSelections).length > 0) {
    setTokenSelections({ ...tokenSelections, ...newTokenSelections });
  }

  // end setting defaults

  const isSpread = !isExcisionWithTips(selections);

  const grossingSegments = modifierSelections?.empty
    ? emptySegment
    : isNail
    ? grossingSegmentsNail
    : isSlowMohs
    ? grossingSegmentsSlowMohs
    : grossingSegmentsSkin;

  const parts = useMemo<
    Array<string | GrossingToken | GrossingConditional>
  >(() => {
    const filterSegment = (
      segment: GrossingSegment | RepeatingGrossingSegments
    ) => !segment.condition || segment.condition(selections, lab?.settings);

    const allowMultipleTokenCounts: {
      [key: keyof typeof grossingTokens]: number;
    } = {};
    const segmentToParts = (segment: GrossingSegment, i?: number) => {
      return segment.content.split(tokenOrConditionalRegex).map((part) => {
        if (tokenRegex.test(part)) {
          const token = grossingTokens[part.substring(1, part.length - 1)];
          if (!token) return part;
          if (complete && token.token === "cassette_count") {
            return token.conditionalOutput
              ? token?.conditionalOutput(
                  selections.tokenSelections?.cassette_count?.value || "",
                  selections
                )
              : selections.tokenSelections?.cassette_count?.value || "";
          }
          if (token.allowMultiple) {
            if (allowMultipleTokenCounts[token.token] !== undefined) {
              allowMultipleTokenCounts[token.token] += 1;
              return {
                ...token,
                token: `${token.token}_${
                  allowMultipleTokenCounts[token.token]
                }`,
              };
            } else {
              allowMultipleTokenCounts[token.token] = 1;
            }
          }
          return token;
        }

        if (conditionalRegex.test(part)) {
          const conditional =
            grossingConditionals[part.substring(1, part.length - 1)];
          if (i !== undefined) {
            return (s: GrossingSelections) => conditional({ ...s, i });
          } else {
            return conditional;
          }
        }

        return part;
      });
    };

    const segments = grossingSegments.filter(filterSegment);

    const tokenRegexStr = "{\\w+(?:\\|\\w+)?}";
    const tokenRegex = new RegExp(`^${tokenRegexStr}$`);
    const conditionalRegexStr = "\\[\\w+\\]";
    const conditionalRegex = new RegExp(`^${conditionalRegexStr}$`);
    const tokenOrConditionalRegex = new RegExp(
      `(${tokenRegexStr}|${conditionalRegexStr})`,
      "g"
    );

    return segments.flatMap((segment) => {
      if ((segment as RepeatingGrossingSegments).countFn) {
        const repeatingSegment = segment as RepeatingGrossingSegments;
        const count = repeatingSegment.countFn(selections);
        if (count < 1) return [];
        const repeatingSegments =
          repeatingSegment.segments.filter(filterSegment);
        return Array.apply(null, Array(count)).flatMap((_, i: number) =>
          repeatingSegments.flatMap((s) => segmentToParts(s, i + 1))
        );
      }

      return segmentToParts(segment as GrossingSegment);
    });
  }, [selections, complete]);

  const tokenParts = useMemo(
    () =>
      parts.filter((part) => (part as GrossingToken).token) as GrossingToken[],
    [parts]
  );

  // Create missing GrossingTokenSelections with default values when parts change.
  useEffect(() => {
    if (tokenParts.length === 0) return;

    const newTokenSelections: {
      [key: keyof typeof grossingTokens]: GrossingTokenSelection;
    } = {};

    tokenParts.forEach((part) => {
      const token = part as GrossingToken;
      if (token.token && !tokenSelections[token.token]) {
        // Auto-select custom input if it is allowed and there are no options available
        if (
          token.allowCustom &&
          token.options.length === 0 &&
          !newTokenSelections[token.token]
        ) {
          newTokenSelections[token.token] = {
            value: undefined,
            default: false,
            custom: true,
          };
        }
      }
    });

    if (Object.keys(newTokenSelections).length > 0)
      setTokenSelections({ ...tokenSelections, ...newTokenSelections });
  }, [currentToken, tokenParts]);

  const allTokensFilled = !tokenParts.some(
    (tokenPart) => tokenSelections[tokenPart.token]?.value === undefined
  );

  const availableTokens = tokenParts.map((token) => token.token);
  const currentTokenIndex = availableTokens.indexOf(`${currentToken}`);

  const selectPrevToken = (wasUser = true) => {
    if (availableTokens.length === 0) {
      setCurrentToken(undefined, wasUser);
    } else if (currentTokenIndex < 1) {
      // Loop around to last token
      setCurrentToken(availableTokens[availableTokens.length - 1], wasUser);
    } else {
      setCurrentToken(availableTokens[currentTokenIndex - 1], wasUser);
    }
  };

  const selectNextToken = (wasUser = true) => {
    if (availableTokens.length === 0) {
      setCurrentToken(undefined, wasUser);
    } else if (!allTokensFilled) {
      // Find and move to next unfilled token
      let i =
        currentTokenIndex === availableTokens.length - 1
          ? 0
          : currentTokenIndex + 1;
      while (i !== currentTokenIndex) {
        if (tokenSelections[availableTokens[i]]?.value === undefined) {
          setCurrentToken(availableTokens[i], wasUser);
          return;
        }
        if (i + 1 < availableTokens.length) {
          i += 1;
        } else {
          i = 0;
        }
      }
      return; // The currentToken is the last unfilled token. Do nothing.
    } else if (currentTokenIndex === availableTokens.length - 1) {
      // Loop around to first token
      setCurrentToken(availableTokens[0], wasUser);
    } else {
      // Move straight to next token
      setCurrentToken(availableTokens[currentTokenIndex + 1], wasUser);
    }
  };

  const gross_description_p = useRef<HTMLParagraphElement>(null);
  const custom_value_input = useRef<HTMLInputElement>(null);

  // Auto-select text input for custom values when it appears
  const currentTokenSelection = currentToken
    ? tokenSelections[currentToken]
    : undefined;
  useEffect(() => {
    if (currentTokenSelection?.custom) {
      custom_value_input.current?.focus();
    }
  }, [currentToken, currentTokenSelection?.custom]);

  const setTokenValue = (
    tokenKey: keyof typeof grossingTokens,
    value: string,
    custom = false
  ) => {
    const token =
      grossingTokens[tokenKey] ||
      grossingTokens[`${tokenKey}`.replace(/_\d+$/, "")];
    const autoFills: GrossingTokenSelections = {};
    if (token?.allowMultiple === "autofill") {
      parts.forEach((part) => {
        // Only autofill parts which are GrossingTokens
        const partToken = part as GrossingToken;
        if (!partToken.token) return;

        // Only autofill parts using the same base token key
        if (!partToken.token.match(new RegExp(`^${token.token}(_\\d+)?$`)))
          return;

        // Only autofill parts which don't already have a selection
        if (tokenSelections[partToken.token]) return;

        // Set autofill value
        autoFills[partToken.token] = {
          value,
          custom,
          default: true,
        };
      });
    }
    let newValue = custom ? value?.trimStart() || undefined : value;
    // Don't allow negative values for cassette_count because it breaks things
    if (tokenKey === "cassette_count" && value && parseInt(value) < 1)
      newValue = undefined;
    setTokenSelections({
      ...tokenSelections,
      ...autoFills,
      [tokenKey]: {
        ...tokenSelections[tokenKey],
        value: newValue,
        custom: custom,
        default: false,
      },
    });
  };

  const resetToDefaultTokens = () => {
    // Keep only token selections where default is true
    setTokenSelections(
      Object.fromEntries(
        Object.entries(tokenSelections).filter(
          ([, selection]) => selection.default === true
        )
      )
    );
    setCurrentToken(undefined, false);
    userHasChangedTokenRef.current = false;
  };

  // Auto-select next unfilled token when a non-custom token value is selected
  const lastTokenRef = useRef(currentToken);
  useEffect(() => {
    const firstUnfilledToken = tokenParts.find(
      (grossingToken) =>
        tokenSelections[grossingToken.token]?.value === undefined
    )?.token;
    if (
      !currentTokenSelection &&
      !userHasChangedTokenRef.current &&
      currentToken !== firstUnfilledToken
    ) {
      setCurrentToken(firstUnfilledToken, false);
    } else if (
      !currentToken ||
      (currentTokenSelection &&
        currentTokenSelection.value !== undefined &&
        !currentTokenSelection.custom &&
        !allTokensFilled &&
        lastTokenRef.current === currentToken) ||
      !availableTokens.includes(`${currentToken}`)
    ) {
      selectNextToken(false);
    }

    lastTokenRef.current = currentToken;
  }, [currentToken, currentTokenSelection, modifierSelections]);

  const [grossingRawText, setGrossingRawText] = useState(
    specimen.grossing_raw_text
  );
  const [rawText, setRawText] = useState(
    (specimen.grossing_raw_text && specimen.gross_description) || undefined
  );

  // Synchronize changes with the server using a debounced function.
  const shouldUpdateRef = useRef(false);
  const isUpdatingSpecimen = useIsLoading(
    `lis/specimens/${specimen.id}/update`
  );
  const debouncedUpdate = useMemo(
    () =>
      debounce(
        (
          grossing_modifier_selections: GrossingModifierSelections,
          grossing_token_selections: GrossingTokenSelections,
          grossing_lab_stain_selections: number[],
          grossing_lab_panel_selections: number[],
          grossing_raw_text?: boolean,
          gross_description?: string,
          bypassRawText?: boolean
        ) => {
          if (!specimen.id) return Promise.reject();
          return dispatch(
            updateSpecimen({
              id: specimen.id,
              requestBody: {
                name: specimen.name || "", // This is a required field in the request body for some reason
                grossing_modifier_selections,
                grossing_token_selections,
                // @ts-ignore
                grossing_lab_stain_selections,
                grossing_lab_panel_selections,
                grossing_raw_text,
                gross_description,
              },
              bypassRawText,
            })
          );
        },
        1000
      ),
    [specimen.id]
  );

  // Auto-update hook
  useEffect(() => {
    // No auto-update if grossing has already been completed and we are editing
    if (complete || !specimen.id) return;

    // We only execute the auto-update after the first render (when a user changes something)
    if (shouldUpdateRef.current) {
      debouncedUpdate(
        modifierSelections,
        tokenSelections,
        labStainSelections,
        labPanelSelections,
        grossingRawText,
        grossingRawText ? rawText : undefined
      );
    }

    shouldUpdateRef.current = true;
  }, [
    specimen.id,
    tokenSelections,
    modifierSelections,
    labStainSelections,
    labPanelSelections,
    grossingRawText,
    rawText,
  ]);

  // Send update request when component is unmounted
  useEffect(() => {
    return () => {
      debouncedUpdate.flush();
    };
  }, [debouncedUpdate]);

  const getDescriptionString = () => {
    return (
      (grossingRawText
        ? rawText
        : gross_description_p.current?.textContent
            ?.replace(/\s+/g, " ")
            .replaceAll(" .", ".")) || ""
    );
  };

  // Deselect cassette_count if it is set to 1 and an incompatible modifier is selected
  useEffect(() => {
    if (
      tokenSelections.cassette_count?.value === "1" &&
      !tokenSelections.cassette_count.custom &&
      (isExcisionWithTips(selections) ||
        isFalseMargins(selections) ||
        isEyeWedge(selections))
    ) {
      const { cassette_count: _, ...newTokenSelections } = tokenSelections;
      setTokenSelections(newTokenSelections);
    }
  }, [tokenSelections, modifierSelections]);

  const prevTokensRef = useRef<GrossingTokenSelections>({});
  useEffect(() => {
    // If a type_cassette_count token is updated, clear any type_cassette_count tokens after it
    const prevTokens = prevTokensRef.current;

    if (
      typeof currentToken === "string" &&
      currentToken.startsWith("type_cassette_count") &&
      prevTokens[currentToken]?.value !== tokenSelections[currentToken]?.value
    ) {
      let updateTokens = false;

      const currentCassetteCount = currentToken.match(
        /type_cassette_count_(\d+)/
      ) || ["type_cassette_count_1", "1"];

      const updatedTokenSelections = { ...tokenSelections };

      Object.keys(tokenSelections)
        .filter((key) => key.startsWith("type_cassette_count_"))
        .sort(
          (a, b) =>
            parseInt(a.replace("type_cassette_count_", "")) -
            parseInt(b.replace("type_cassette_count_", ""))
        )
        .forEach((key) => {
          if (
            parseInt(key.replace("type_cassette_count_", "")) >
              parseInt(currentCassetteCount[1]) &&
            tokenSelections[key]?.value !== undefined
          ) {
            updateTokens = true;
            delete updatedTokenSelections[key];
          }
        });

      if (updateTokens) {
        setTokenSelections(updatedTokenSelections);
      }

      prevTokensRef.current = updatedTokenSelections;
    }

    // If multiple shaves is selected, and type or cassette count is selected, clear all type_cassette_count tokens. If type count is changed, also clear cassette count.
    if (
      (currentToken === "type_count" || currentToken === "cassette_count") &&
      (prevTokens.type_count?.value !== tokenSelections.type_count?.value ||
        prevTokens.cassette_count?.value !==
          tokenSelections.cassette_count?.value) &&
      modifierSelections.multipleShaves
    ) {
      let updateTokens = false;

      const updatedTokenSelections = { ...tokenSelections };

      if (
        currentToken === "type_count" &&
        updatedTokenSelections.cassette_count
      ) {
        updateTokens = true;
        delete updatedTokenSelections.cassette_count;
      }

      Object.keys(tokenSelections).forEach((key) => {
        if (key.startsWith("type_cassette_count")) {
          updateTokens = true;
          delete updatedTokenSelections[key];
        }

        if (key === "cassette_count" && currentToken === "type_count") {
          updateTokens = true;
          delete updatedTokenSelections[key];
        }
      });

      if (updateTokens) {
        setTokenSelections(updatedTokenSelections);
      }

      prevTokensRef.current = updatedTokenSelections;
    }
  }, [currentToken, modifierSelections.multipleShaves, tokenSelections]);

  const toggleTextEditor = useCallback(() => {
    setRawText(!grossingRawText ? getDescriptionString() : undefined);
    setGrossingRawText(!grossingRawText);
  }, [specimen.id, grossingRawText]);

  return {
    modifierSelections,
    toggleModifier,
    currentToken,
    setCurrentToken,
    tokenSelections,
    setTokenValue,
    labStainSelections,
    toggleLabStain,
    labPanelSelections,
    toggleLabPanel,
    allTokensFilled,
    cassetteCount,
    selectPrevToken,
    selectNextToken,
    parts,
    gross_description_p,
    custom_value_input,
    getDescriptionString,
    debouncedUpdate,
    isUpdatingSpecimen,
    rawText,
    setRawText,
    hasTips: isExcisionWithTips(selections),
    isShaveFragments: isShaveFragments(selections),
    isFalseMargins: isFalseMargins(selections),
    isEyeWedge: isEyeWedge(selections),
    isDIFSelected: isDIFSelected(selections),
    isSpread,
    toggleTextEditor,
    grossingRawText,
    tokenParts,
    resetToDefaultTokens,
  };
};

export default useGrossingMadLib;
